import React, { useRef, useEffect, useCallback } from "react";
import { Loader } from "../core/Loader/Loader";
import { StyledList, StyledObserverTarget } from "./InfiniteScrollList.styled";

interface InfiniteScrollListProps<T> {
  items: T[];
  renderItem: (item: T, index: number) => React.ReactNode;
  onScroll: (item?: T) => void;
  canLoadMore: boolean;
  loading?: boolean;
  fetchingMore?: boolean;
  onTopItemChange?: (item: T) => void;
  containerHeight?: number;
  gapElementList?: number;
}

export function InfiniteScrollList<T>({
  items,
  renderItem,
  onScroll,
  canLoadMore,
  loading = false,
  fetchingMore = false,
  onTopItemChange,
  gapElementList = 12,
}: InfiniteScrollListProps<T>) {
  const observerTarget = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const itemRefs = useRef<Map<number, HTMLDivElement>>(new Map());
  const topItemObserverRef = useRef<IntersectionObserver | null>(null);

  useEffect(() => {
    if (loading && listRef.current) {
      listRef.current.scrollTop = 0;
    }
  }, [loading]);

  // Observer for load more functionality
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (
          entries[0].isIntersecting &&
          canLoadMore &&
          !fetchingMore &&
          !loading
        ) {
          onScroll();
        }
      },
      { threshold: 0.1, root: listRef.current },
    );

    const currentTarget = observerTarget.current;
    if (currentTarget) {
      observer.observe(currentTarget);
    }

    return () => {
      if (currentTarget) {
        observer.unobserve(currentTarget);
      }
    };
  }, [canLoadMore, fetchingMore, onScroll, loading]);

  // Observer for tracking top item
  useEffect(() => {
    if (!onTopItemChange || !listRef.current) return;

    // Clean up previous observer
    if (topItemObserverRef.current) {
      topItemObserverRef.current.disconnect();
    }

    const handleScroll = () => {
      if (!listRef.current) return;

      const containerTop = listRef.current.getBoundingClientRect().top;
      let topItem: T | null = null;
      let minDistance = Infinity;

      // Find the item closest to the top of the container
      itemRefs.current.forEach((element, index) => {
        if (element && items[index]) {
          const elementTop = element.getBoundingClientRect().top;
          const distance = Math.abs(elementTop - containerTop);

          // Check if element is visible and above or at the top
          if (elementTop <= containerTop + 10 && distance < minDistance) {
            minDistance = distance;
            topItem = items[index];
          }
        }
      });

      // If no item found above, get the first visible item
      if (!topItem) {
        for (let i = 0; i < items.length; i++) {
          const element = itemRefs.current.get(i);
          if (element) {
            const rect = element.getBoundingClientRect();
            const containerRect = listRef.current.getBoundingClientRect();
            if (
              rect.top >= containerRect.top &&
              rect.top <= containerRect.bottom
            ) {
              topItem = items[i];
              break;
            }
          }
        }
      }

      if (topItem) {
        onTopItemChange(topItem);
      }
    };

    // Create observer to track when items enter/exit viewport
    topItemObserverRef.current = new IntersectionObserver(
      () => {
        handleScroll();
      },
      {
        root: listRef.current,
        rootMargin: "0px",
        threshold: [0, 0.1],
      },
    );

    // Observe all items
    itemRefs.current.forEach((element) => {
      if (element) {
        topItemObserverRef.current?.observe(element);
      }
    });

    // Also listen to scroll events for more accurate tracking
    const listElement = listRef.current;
    listElement?.addEventListener("scroll", handleScroll);

    // Initial check
    handleScroll();

    return () => {
      if (topItemObserverRef.current) {
        topItemObserverRef.current.disconnect();
      }
      listElement?.removeEventListener("scroll", handleScroll);
    };
  }, [items, onTopItemChange]);

  const setItemRef = useCallback((index: number) => {
    return (element: HTMLDivElement | null) => {
      if (element) {
        itemRefs.current.set(index, element);
        // Observe the new element if observer exists
        if (topItemObserverRef.current) {
          topItemObserverRef.current.observe(element);
        }
      } else {
        const existingElement = itemRefs.current.get(index);
        if (existingElement && topItemObserverRef.current) {
          topItemObserverRef.current.unobserve(existingElement);
        }
        itemRefs.current.delete(index);
      }
    };
  }, []);

  return (
    <StyledList ref={listRef} $gapElementList={gapElementList}>
      {items.map((item, index) => (
        <div key={index} ref={setItemRef(index)}>
          {renderItem(item, index)}
        </div>
      ))}
      {(fetchingMore || loading) && <Loader loading size={24} />}
      <StyledObserverTarget ref={observerTarget} />
    </StyledList>
  );
}
